import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, Loader2 } from "lucide-react";

interface LevelStats {
  level: number;
  total: number;
  withContent: number;
}

export const CategorySeoContentGenerator: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [running, setRunning] = useState<number | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  const { data: stats, refetch } = useQuery({
    queryKey: ["category-seo-content-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("level, seo_content");
      if (error) throw error;
      const out: Record<number, LevelStats> = {};
      (data || []).forEach((c: any) => {
        const lvl = c.level ?? 0;
        if (!out[lvl]) out[lvl] = { level: lvl, total: 0, withContent: 0 };
        out[lvl].total += 1;
        if (c.seo_content && String(c.seo_content).trim()) {
          out[lvl].withContent += 1;
        }
      });
      return Object.values(out).sort((a, b) => a.level - b.level);
    },
  });

  const runBatch = async (level: number) => {
    setRunning(level);
    setLog([`Starting Level ${level} generation${overwrite ? " (overwrite mode)" : ""}...`]);
    try {
      let totalProcessed = 0;
      let totalSuccess = 0;
      // Loop until no more to process (batchSize=5 per call to stay under timeouts)
      // Cap at 200 iterations as safety.
      for (let i = 0; i < 200; i++) {
        const { data, error } = await supabase.functions.invoke(
          "generate-category-seo-content",
          { body: { level, batchSize: 5, overwrite } }
        );
        if (error) throw error;
        const processed = data?.processed ?? 0;
        const total = data?.total ?? 0;
        totalProcessed += total;
        totalSuccess += processed;
        setLog((l) => [
          ...l,
          `Batch ${i + 1}: ${processed}/${total} succeeded (${data?.message ?? ""})`,
        ]);
        if (!total || overwrite) {
          // overwrite mode would loop forever; do one batch only
          if (overwrite) break;
          break;
        }
      }
      toast({
        title: "Generation complete",
        description: `Level ${level}: ${totalSuccess}/${totalProcessed} succeeded`,
      });
      await refetch();
      queryClient.invalidateQueries({ queryKey: ["category-seo-content-stats"] });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error",
        description: err.message || "Generation failed",
        variant: "destructive",
      });
    } finally {
      setRunning(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          Category SEO Content
        </CardTitle>
        <CardDescription>
          Generates 400-550 word AI body content for category pages to improve
          SEO. Tailored to youth/minor baseball (ages 5–18, not MLB).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="overwrite"
            checked={overwrite}
            onCheckedChange={(v) => setOverwrite(!!v)}
          />
          <label htmlFor="overwrite" className="text-sm">
            Overwrite existing content (otherwise only fills empty ones)
          </label>
        </div>

        <div className="space-y-2">
          {stats?.map((s) => (
            <div
              key={s.level}
              className="flex items-center justify-between border rounded-md p-3"
            >
              <div className="flex items-center gap-3">
                <Badge variant="outline">Level {s.level}</Badge>
                <span className="text-sm">
                  {s.withContent}/{s.total} have content
                </span>
                {s.level >= 3 && (
                  <span className="text-xs text-amber-600">
                    Large batch — slow & uses many AI credits
                  </span>
                )}
              </div>
              <Button
                size="sm"
                disabled={running !== null}
                onClick={() => runBatch(s.level)}
              >
                {running === s.level ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>Generate Level {s.level}</>
                )}
              </Button>
            </div>
          ))}
        </div>

        {log.length > 0 && (
          <div className="mt-4 p-3 bg-muted rounded-md max-h-64 overflow-y-auto text-xs font-mono space-y-1">
            {log.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
